import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Check } from 'lucide-react';
import { getErrorMessage } from '@/utils/helpers';

export default function SignupPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    business_name: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await signup(formData);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = getErrorMessage(error, 'Something went wrong');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'GST-compliant invoicing in 60 seconds',
    '14 days of the Growth plan free, no card needed',
    'Expense & ITC tracking on every purchase',
    'AI compliance co-pilot (Arya)',
    'Invite your CA and import from Tally, Vyapar or Excel'
  ];

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Left side - Benefits */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0F3D2A] to-[#06100A] p-12 items-center justify-center">
        <div className="max-w-lg text-white">
          <h2 className="text-4xl font-bold font-['Outfit'] mb-6">
            Start your free trial
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Your free trial starts the moment you sign up. After 14 days you move to the Free plan and keep all your data.
          </p>
          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3">
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span className="text-emerald-100">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md space-y-8">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 mb-8">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
            
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-[#1D9E75] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl font-['Outfit']">L</span>
              </div>
              <span className="text-2xl font-bold text-stone-900 font-['Outfit']">Laksh</span>
            </div>
            
            <h1 className="text-3xl font-bold text-stone-900 font-['Outfit']">
              Create your account
            </h1>
            <p className="text-stone-600 mt-2">
              Get started with Laksh in minutes
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-form">
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                required
                data-testid="signup-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="signup-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone number</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="+91 98765 43210"
                value={formData.phone}
                onChange={handleChange}
                required
                data-testid="signup-phone"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_name">Business name (optional)</Label>
              <Input
                id="business_name"
                name="business_name"
                type="text"
                placeholder="Acme Corp"
                value={formData.business_name}
                onChange={handleChange}
                data-testid="signup-business"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Minimum 6 characters"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1D9E75] hover:bg-[#178a65] text-white btn-press"
              data-testid="signup-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </Button>

            <p className="text-xs text-stone-500 text-center">
              By signing up, you agree to our Terms of Service and Privacy Policy.
            </p>
          </form>

          <p className="text-center text-stone-600">
            Already have an account?{' '}
            <Link to="/login" className="text-[#1D9E75] hover:text-[#178a65] font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
